import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { getExtendedMetadata } from '@/types/treatment-record-request-extended';
import {
  getNextCallDate,
  getVoiceAIStatusBadgeColor,
  getVoiceAIStatusMessage,
  isVoiceAIApplicable,
  willVoiceAIBeApplicable,
} from '@/utils/voiceAiScheduling';

import {
  DEFAULT_VOICE_AI_INTERVAL,
  VOICE_AI_EDITABLE_STATUSES,
} from '../../../../constants';

/**
 * Custom hook to manage Voice AI status and related data
 * @param request - The treatment record request
 * @returns Voice AI status information
 */
export function useVoiceAIStatus(request: TreatmentRecordRequestSchema) {
  const extendedMetadata = getExtendedMetadata(request.metadata);
  const interval =
    extendedMetadata.voice_ai_check_interval_days || DEFAULT_VOICE_AI_INTERVAL;
  const lastCalledAt = extendedMetadata.last_called_at || null;
  const nextScheduledCall = extendedMetadata.next_scheduled_call || null;

  const isActive = isVoiceAIApplicable(request.channel?.type, request.status);
  const willBeActive =
    request.status === 'pending' &&
    willVoiceAIBeApplicable(request.channel?.type);
  const isPortal = request.channel?.type === 'portal';

  // Simply get the next call date from backend-provided value
  const nextCallDate = getNextCallDate(nextScheduledCall);

  const canEdit =
    !isPortal &&
    request.status &&
    VOICE_AI_EDITABLE_STATUSES.includes(request.status);

  return {
    interval,
    lastCalledAt,
    isActive,
    willBeActive,
    isPortal,
    nextCallDate,
    canEdit,
    statusMessage: getVoiceAIStatusMessage(
      request.channel?.type,
      request.status,
    ),
    badgeColor: getVoiceAIStatusBadgeColor(
      request.channel?.type,
      request.status,
    ),
  };
}
